import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { scrollToSection } from "./Nav";
import { CONTACT, IMAGES } from "../data/content";
import { ArrowDown, MessageCircle } from "lucide-react";

const EASE = [0.22, 1, 0.36, 1];

const MaskedLine = ({ children, i }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className="block"
      initial={{ y: "115%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1.1, delay: 0.25 + i * 0.14, ease: EASE }}
    >
      {children}
    </motion.span>
  </span>
);

const FadeUp = ({ delay, className, children }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.9, delay }}
    className={className}
  >
    {children}
  </motion.div>
);

const HeroCopy = () => (
  <>
    <FadeUp delay={0.1} className="flex items-center gap-3 mb-8">
      <span className="h-px w-10 bg-[#1E5FE0]" />
      <p className="f-label text-[13px] uppercase tracking-[0.2em] text-[#1E5FE0]">
        Management & Institutional Consultancy — Pan India
      </p>
    </FadeUp>

    <h1
      data-testid="hero-headline"
      className="f-display font-bold text-[#0B2A5B] text-[13vw] sm:text-6xl lg:text-[5.2rem] leading-[1.02] tracking-[-0.02em]"
    >
      <MaskedLine i={0}>Built on legacy&hellip;</MaskedLine>
      <MaskedLine i={1}>
        Designed for{" "}
        <em className="not-italic text-[#1E5FE0]">What&rsquo;s&nbsp;next!</em>
      </MaskedLine>
    </h1>

    <FadeUp delay={0.75}>
      <p data-testid="hero-subheadline" className="mt-8 max-w-xl text-base sm:text-lg text-slate-600 leading-relaxed">
        A management and institutional consulting firm with three decades of
        Synergy experience behind it — and the future of Indian organisations
        ahead of it. We partner with owners, promoters, doctors, educators and
        leadership teams to strengthen strategy, develop people and deliver
        measurable impact.
      </p>
    </FadeUp>

    <FadeUp delay={0.95} className="mt-10 flex flex-wrap items-center gap-4">
      <a
        href={CONTACT.whatsappLink}
        target="_blank"
        rel="noopener noreferrer"
        data-testid="hero-whatsapp-button"
        className="group flex items-center gap-3 rounded-full bg-[#0B2A5B] text-white px-7 py-4 f-label text-sm uppercase tracking-[0.18em] hover:bg-[#1E5FE0] transition-all duration-300 hover:-translate-y-0.5 shadow-[0_18px_40px_-16px_rgba(11,42,91,0.5)]"
      >
        <MessageCircle className="w-4 h-4 transition-transform duration-300 group-hover:rotate-12" />
        Start a conversation
      </a>
      <button
        onClick={() => scrollToSection("#sectors")}
        data-testid="hero-explore-button"
        className="group flex items-center gap-3 rounded-full border border-[#0B2A5B]/25 text-[#0B2A5B] px-7 py-4 f-label text-sm uppercase tracking-[0.18em] hover:border-[#1E5FE0] hover:text-[#1E5FE0] transition-all duration-300"
      >
        Explore our sectors
        <ArrowDown className="w-4 h-4 transition-transform duration-300 group-hover:translate-y-1" />
      </button>
    </FadeUp>
  </>
);

const HeroArt = () => (
  <motion.div
    initial={{ opacity: 0, scale: 0.92, rotate: -2 }}
    animate={{ opacity: 1, scale: 1, rotate: 0 }}
    transition={{ duration: 1.4, delay: 0.4, ease: EASE }}
    className="relative w-full lg:w-[min(28rem,calc((100vh-230px)*0.8))] ml-auto"
  >
    <div className="relative aspect-[4/3] sm:aspect-[16/9] lg:aspect-[4/5] rounded-[2rem] bg-[#0B2A5B] overflow-hidden">
      <img
        src={IMAGES.boardroom}
        alt="Indian leadership team in a strategy discussion"
        data-testid="hero-image"
        className="absolute inset-0 w-full h-full object-cover"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-[#0B2A5B] via-[#0B2A5B]/55 to-[#0B2A5B]/10" />
      <img src={IMAGES.logoMark} alt="" className="absolute top-5 left-5 lg:top-7 lg:left-7 h-8 lg:h-10 w-auto drop-shadow-[0_2px_8px_rgba(255,255,255,0.9)]" />
      <p className="absolute bottom-6 lg:bottom-32 left-6 right-6 lg:left-8 lg:right-8 f-display text-white text-lg sm:text-xl lg:text-2xl leading-snug">
        &ldquo;Organisations rarely fail for lack of ideas; they struggle
        when strategy, people and execution move apart.&rdquo;
      </p>
    </div>
    <div className="absolute -left-10 top-10 spin-slow hidden lg:block">
      <div className="w-24 h-24 rounded-full border border-dashed border-[#1E5FE0]/50" />
    </div>
    <motion.div
      animate={{ y: [0, -12, 0] }}
      transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
      className="mt-4 ml-auto w-fit lg:mt-0 lg:w-auto lg:absolute lg:-right-4 lg:-bottom-8 max-w-[260px] bg-white rounded-2xl shadow-[0_24px_60px_-20px_rgba(11,42,91,0.35)] border border-[#0B2A5B]/10 px-5 py-4 lg:px-6 lg:py-5"
    >
      <p className="f-label text-xs uppercase tracking-[0.2em] text-[#1E5FE0]">Est. legacy</p>
      <p className="f-display text-[#0B2A5B] text-base lg:text-lg leading-snug mt-1">Synergy Communications &rarr; Synergy Next</p>
    </motion.div>
  </motion.div>
);

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yArt = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const yText = useTransform(scrollYProgress, [0, 1], [0, 60]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section
      id="top"
      ref={ref}
      data-testid="hero-section"
      className="blueprint relative lg:min-h-screen flex items-center overflow-hidden pt-[70px]"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 w-full grid grid-cols-1 lg:grid-cols-12 gap-14 lg:gap-6 items-center py-14 sm:py-20 lg:py-0">
        <motion.div style={{ y: yText, opacity }} className="lg:col-span-7 relative z-10">
          <HeroCopy />
        </motion.div>
        <motion.div style={{ y: yArt }} className="lg:col-span-5 relative">
          <HeroArt />
        </motion.div>
      </div>
    </section>
  );
};
